import { useEffect, useRef, useState } from 'react'
import { View } from 'react-native'
import { useTranslation } from 'react-i18next'
import Animated, {
  measure,
  MeasuredDimensions,
  useAnimatedRef,
  useAnimatedScrollHandler,
  useSharedValue,
} from 'react-native-reanimated'
import { scheduleOnUI } from 'react-native-worklets'
import { useLocalSearchParams } from 'expo-router'
import { topbar } from '@ui/theme/values'
import Page from '@shell/Page'
import Header from '@ui/elements/Header'
import EmptyResult from '@ui/components/EmptyResult'
import { trpc } from 'api'
import { useToast } from '@ui/toasts/useToastManager'
import { useAnimatedOpacity } from '@ui/animated/useAnimatedOpacity'
import AnimatedEmpty from '@ui/animated/AnimatedEmpty'
import OrderHistoryTile from '@order/OrderHistory/Tile'
import { useConfigConditionValue } from 'features/config/useConfigConditionValue'
import { ORDER_HISTORY } from 'features/config/ConfigConditionSchemas'

import Loading from './Loading'
import PaymentBanner from './sections/PaymentBanner'
import General from './sections/General'
import Frame from './sections/Frame'
import Refraction from './sections/Refraction'
import Configuration from './sections/Configuration'
import Receipt from './sections/Receipt'
import Payments from './sections/Payments'

export default function CustomerOrderDetails() {
  const toast = useToast()
  const { id } = useLocalSearchParams<{ id: string }>()

  const { t } = useTranslation(['common', 'ui', 'order', 'toasts'])
  const text = 'order:details'

  const {
    data: data,
    isLoading,
    isFetched,
    error,
  } = trpc.order.getById.useQuery(
    {
      id: parseInt(id),
    },
    {
      enabled: !!id,
      refetchOnWindowFocus: false,
    }
  )

  useEffect(() => {
    if (!error) {
      return
    }
    if (error.data?.code === 'UNAUTHORIZED') {
      return
    }
    toast({
      variant: 'error',
      title: t('toasts:error.order.details.title'),
      text: error.message ?? t('toasts:error.order.details.desc'),
    })
  }, [error, t, toast])

  const showLoading = isLoading && !!id
  const showEmpty = !data && !isLoading && isFetched

  const styleEmpty = useAnimatedOpacity(showEmpty)
  const styleLoaded = useAnimatedOpacity(!showLoading)

  const orderHistory = useConfigConditionValue(ORDER_HISTORY, 'orderHistory')
  const showPayments = orderHistory?.payments
  const canPay = orderHistory?.pay ?? false

  const scrollRef = useRef<Animated.ScrollView>(null)
  const paymentsRef = useAnimatedRef<View>()
  const scrollY = useSharedValue(0)
  const measured = useSharedValue<MeasuredDimensions | null>(null)
  const [openPaymentsSignal, setOpenPaymentsSignal] = useState(0)

  const onScroll = useAnimatedScrollHandler({
    onScroll: (event) => {
      measured.set(measure(paymentsRef))
      scrollY.set(event.contentOffset.y)
    },
  })

  const handlePaymentsLayout = () => {
    scheduleOnUI(() => {
      'worklet'
      measured.set(measure(paymentsRef))
    })
  }

  const scrollToPayments = () => {
    setOpenPaymentsSignal((n) => n + 1)
    if (!measured.value) {
      return
    }
    scrollRef.current?.scrollTo({
      y: Math.max(scrollY.value + measured.value.pageY - topbar.height - 24, 0),
      animated: true,
    })
  }

  return (
    <>
      <Page stage="customer" scrollViewRef={scrollRef} onScroll={onScroll}>
        <Header
          title={t(`${text}.header.title`)}
          desc={t(`${text}.header.desc`)}
        />
        {showLoading && <Loading />}
        <Animated.View
          style={styleLoaded}
          className="flex flex-col justify-start gap-8 px-8"
        >
          {data && (
            <>
              <View>
                <OrderHistoryTile data={data} />
                {canPay && (
                  <PaymentBanner
                    key={data.id}
                    data={data}
                    onShowPayments={showPayments ? scrollToPayments : undefined}
                  />
                )}
              </View>
              <General data={data} />
              <Frame data={data} />
              <Refraction data={data} />
              <Configuration data={data} />
              <Receipt data={data} />
              {showPayments && (
                <View
                  ref={paymentsRef}
                  collapsable={false}
                  onLayout={handlePaymentsLayout}
                >
                  <Payments data={data} openSignal={openPaymentsSignal} />
                </View>
              )}
            </>
          )}
        </Animated.View>
        <AnimatedEmpty style={styleEmpty} className="pt-0">
          <EmptyResult
            title={t(`${text}.empty.result.title`)}
            desc={t(`${text}.empty.result.desc`)}
          />
        </AnimatedEmpty>
      </Page>
    </>
  )
}
